// Issue metadata card — status, priority, category, version, module, customer, assignee
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Info, Building2 } from 'lucide-react'

interface IssueMetadataCardProps {
  issue: Record<string, unknown>
}

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-kaycoo-blue-pale text-kaycoo-blue-dark border-kaycoo-blue',
  in_progress: 'bg-amber-50 text-amber-800 border-amber-300',
  waiting_info: 'bg-purple-50 text-purple-800 border-purple-300',
  resolved: 'bg-kaycoo-teal-pale text-kaycoo-teal-dark border-kaycoo-teal',
  closed: 'bg-kaycoo-white-2 text-kaycoo-charcoal-2 border-kaycoo-white-3',
}

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-kaycoo-white-2 text-kaycoo-charcoal-2',
  medium: 'bg-kaycoo-blue-pale text-kaycoo-blue-dark',
  high: 'bg-amber-50 text-amber-800',
  critical: 'bg-kaycoo-burgundy-pale text-kaycoo-burgundy-dark',
}

function MetadataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-kaycoo-charcoal-2">{label}</span>
      <span className="font-medium text-kaycoo-charcoal">{value ?? '—'}</span>
    </div>
  )
}

export function IssueMetadataCard({ issue }: IssueMetadataCardProps): JSX.Element {
  const status = String(issue.status ?? 'open')
  const priority = String(issue.priority ?? 'medium')

  return (
    <>
    <Card className="border-kaycoo-teal-light bg-kaycoo-white">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm text-kaycoo-teal-dark">
          <Info className="h-4 w-4" />
          Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 divide-y divide-kaycoo-white-3">
        <MetadataRow
          label="Status"
          value={
            <Badge variant="outline" className={STATUS_COLORS[status] ?? ''}>
              {status.replace('_', ' ')}
            </Badge>
          }
        />
        <MetadataRow
          label="Priority"
          value={
            <Badge variant="outline" className={PRIORITY_COLORS[priority] ?? ''}>
              {priority}
            </Badge>
          }
        />
        <MetadataRow label="Category" value={String(issue.category ?? '—')} />
        <MetadataRow label="D365 Version" value={String(issue.d365_version ?? '—')} />
        <MetadataRow label="Module" value={String(issue.d365_module ?? '—')} />
        <MetadataRow label="Expertise Domain" value={String(issue.expertise_domain ?? '—')} />
        <MetadataRow label="Deployment" value={String(issue.d365_deployment_type ?? '—')} />
        <MetadataRow
          label="Created"
          value={issue.created_at ? new Date(String(issue.created_at)).toLocaleDateString() : '—'}
        />
        <MetadataRow label="Assigned To" value={String(issue.assigned_to ?? 'Unassigned')} />
      </CardContent>
    </Card>

    {/* Customer Info */}
    {Boolean(issue.customer_name || issue.customer_email || issue.contact_person || issue.industry) && (
      <Card className="border-kaycoo-teal-light bg-kaycoo-white">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm text-kaycoo-teal-dark">
            <Building2 className="h-4 w-4" />
            Customer
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 divide-y divide-kaycoo-white-3">
          {Boolean(issue.customer_name) && <MetadataRow label="Company" value={String(issue.customer_name)} />}
          {Boolean(issue.customer_email) && <MetadataRow label="Email" value={String(issue.customer_email)} />}
          {Boolean(issue.contact_person) && <MetadataRow label="Contact" value={String(issue.contact_person)} />}
          {Boolean(issue.contact_role) && <MetadataRow label="Role" value={String(issue.contact_role)} />}
          {Boolean(issue.industry) && <MetadataRow label="Industry" value={String(issue.industry)} />}
        </CardContent>
      </Card>
    )}
    </>
  )
}
